"use client";

import Image from "next/image";

import type { AudioSample, ContentGridBlock, Media } from "@mw/shared/payload-types";
import { filterEntitiesArray, getImageProps, getVideoUrl } from "@utilities/payload";
import { ImageSizes } from "@ui/lib/image";
import { BarChart, type BarChartConfig } from "../BarChart";
import { ChartVideo, type VideoConfig } from "../ChartVideo";
import { LazyComponent } from "../BlockBentoGrid/LazyComponent";
import { TableSimple, type ColumnColor } from "@ui/components/core/TableSimple";

type ContentGridItem = NonNullable<ContentGridBlock["items"]>[number];
type ContentGridMainContent = NonNullable<ContentGridItem["mainContent"]>;
type ContentGridFeature = NonNullable<NonNullable<ContentGridMainContent["featuresList"]>["features"]>[number];
type ContentGridDemoFeature = Omit<ContentGridFeature, "icon"> & { icon?: React.ReactNode };

type ContentGridDemoMainContent = Omit<ContentGridMainContent, "featuresList"> & {
  featuresList?: Omit<NonNullable<ContentGridMainContent["featuresList"]>, "features"> & {
    features?: ContentGridDemoFeature[] | null;
  };
};

export type ContentGridDemoItem = Omit<ContentGridItem, "title" | "mainContent"> & {
  title: string;
  mainContent?: ContentGridDemoMainContent | null;
};

const getFeatureColor = (style: ContentGridFeature["style"]): ColumnColor => (style === "muted" ? "gray" : "default");

interface ContentGridDemoContentProps {
  item: ContentGridDemoItem;
  /** Pre-rendered icon element from the server component (CentralIcon is server-only) */
  iconElement?: React.ReactNode;
}

/**
 * Renders demo components based on the component template (client-side only demos)
 * Uses the shared LazyComponent abstraction to avoid duplicating dynamic imports.
 */
const DemoComponent = ({ componentData }: { componentData: any }) => {
  const template = componentData.template;

  switch (template) {
    case "multimodalAgentsDemo":
      return <LazyComponent name='multimodalAgentsDemo' />;

    case "musicUIDemo":
      return <LazyComponent name='musicUIDemo' />;

    case "musicV2UIDemo":
      return <LazyComponent name='musicV2UIDemo' />;

    case "musicV2InpaintingDemo":
      return <LazyComponent name='musicV2InpaintingDemo' />;

    case "musicV2MarketplaceDemo":
      return <LazyComponent name='musicV2MarketplaceDemo' />;

    case "dubbingV2Demo":
      return <LazyComponent name='dubbingV2Demo' />;

    case "elevenMusicDemo":
      return <LazyComponent name='elevenMusicDemo' />;

    case "allInOneEditorDemo": {
      const data = componentData.allInOneEditorDemo;
      return (
        <LazyComponent
          name='allInOneEditorDemo'
          backgroundColor={data?.backgroundColor ?? "cream"}
          mainText={data?.mainText}
          secondaryText={data?.secondaryText}
          timelineImage={data?.timelineImage}
          frameImage={data?.frameImage}
        />
      );
    }

    case "chartsDemo": {
      const charts = componentData.chartsDemo?.charts;
      return (
        <LazyComponent
          name='chartsDemo'
          charts={charts?.map((chart: any) => ({
            label: chart.label,
            chart: {
              ...chart.chart,
              type: "bar",
            },
          }))}
        />
      );
    }

    case "languagesGlobeDemo":
      return <LazyComponent name='languagesGlobeDemo' />;

    case "scribeV1toV2Demo":
      return <LazyComponent name='scribeV1toV2Demo' />;

    case "callTranscriptionDemo":
      return <LazyComponent name='callTranscriptionDemo' />;

    case "wordPredictionDemo":
      return <LazyComponent name='wordPredictionDemo' />;

    case "beforeAfterDemo": {
      const data = componentData.beforeAfterDemo;
      return (
        <LazyComponent
          name='beforeAfterDemo'
          beforeImage={data?.beforeImage}
          afterImage={data?.afterImage}
          beforeLabel={data?.beforeLabel}
          afterLabel={data?.afterLabel}
        />
      );
    }

    case "lipSyncDemo":
      return <LazyComponent name='lipSyncDemo' />;

    case "lipSyncAvatarDemo":
      return <LazyComponent name='lipSyncAvatarDemo' />;

    case "ttsControlsDemo":
      return <LazyComponent name='ttsControlsDemo' />;

    case "voiceCloneDemo":
      return <LazyComponent name='voiceCloneDemo' />;

    case "videoScrollerDemo": {
      const videoCards = componentData.videoScrollerDemo?.videoCards ?? [];
      return (
        <LazyComponent
          name='videoScrollerDemo'
          cards={videoCards.map((card: any, index: number) => {
            const videoUrl = card.video ? getVideoUrl(card.video) : "";
            const coverImageProps = card.coverImage ? getImageProps(card.coverImage) : null;

            return {
              id: `video-card-${index}`,
              video: videoUrl || "",
              title: card.title,
              coverImage: coverImageProps,
            };
          })}
        />
      );
    }

    case "voiceCompareDemo": {
      const data = componentData.voiceCompareDemo;
      return <LazyComponent name='voiceCompareDemo' leftDisc={data?.leftDisc} rightDisc={data?.rightDisc} />;
    }

    case "simpleMusicDemo":
      return <LazyComponent name='simpleMusicDemo' />;

    case "videoComparisonDemo": {
      const data = componentData.videoComparisonDemo;
      return <LazyComponent name='videoComparisonDemo' videos={data?.videos} />;
    }

    case "audioTranscriptDemo": {
      const data = componentData.audioTranscriptDemo;
      const audioItems = (filterEntitiesArray(data?.items) || []) as AudioSample[];
      if (audioItems.length === 0) return null;
      return <LazyComponent name='audioTranscriptDemo' items={audioItems} playbackVariant={data?.playbackVariant} />;
    }

    default:
      return null;
  }
};

const ContentWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className='tw-p-5 md:tw-p-8 lg:tw-p-12 tw-h-[21.625rem] md:tw-h-[25.5rem] lg:tw-h-[27.5rem] tw-w-full tw-flex tw-items-end [&>*]:tw-w-full'>
    {children}
  </div>
);

/**
 * Client component that renders the main content for a content grid item based on the mainContent template.
 * This is separated from the main BlockContentGrid to allow using "use client" for demo components
 * while keeping the main component as a server component.
 */
export const ContentGridDemoContent = ({ item, iconElement }: ContentGridDemoContentProps) => {
  const { mainContent } = item;

  // Handle legacy image field for backward compatibility
  const legacyImageProps = item.image ? getImageProps(item.image) : null;

  // If no mainContent or template is "none", fall back to legacy image
  if (!mainContent || mainContent.template === "none") {
    if (legacyImageProps) {
      return (
        <Image
          {...legacyImageProps}
          alt={legacyImageProps.alt || item.title}
          className='tw-object-contain tw-object-right-bottom'
          style={{ aspectRatio: "27/16" }}
          sizes={ImageSizes.FULL_HALF_ON_LG}
          quality={100}
        />
      );
    }
    return null;
  }

  switch (mainContent.template) {
    case "featuresList": {
      const featuresList = item.mainContent?.featuresList?.features;

      if (!featuresList?.length) {
        return null;
      }

      return (
        <div className='tw-px-5 md:tw-px-8 lg:tw-px-12'>
          <TableSimple
            endingBorder={false}
            columns={[{ key: "feature" }]}
            rows={featuresList.map(feature => ({
              feature: {
                color: getFeatureColor(feature.style),
                icon: feature.icon,
                value: feature.description,
              },
            }))}
            withHeaderHighlighted={false}
          />
        </div>
      );
    }
    case "icon":
      return iconElement ? (
        <div className='tw-p-5 md:tw-p-8 lg:tw-p-12 tw-pb-0 md:tw-pb-0 lg:tw-pb-0'>{iconElement}</div>
      ) : null;

    case "image": {
      const imageData = mainContent.image;
      const imageProps = imageData?.image ? getImageProps(imageData.image) : null;
      if (!imageProps) return null;

      return (
        <Image
          {...imageProps}
          alt={imageProps.alt || item.title}
          className='tw-object-contain tw-object-right-bottom'
          style={{ aspectRatio: "27/16" }}
          sizes={ImageSizes.FULL_HALF_ON_LG}
          quality={100}
        />
      );
    }

    case "inverseMedia": {
      const imageProps = mainContent.inverseMedia?.image ? getImageProps(mainContent.inverseMedia.image) : null;
      const inverseImageProps = mainContent.inverseMedia?.imageInverse
        ? getImageProps(mainContent.inverseMedia.imageInverse)
        : null;
      if (!imageProps) return null;
      return (
        <div className='tw-flex tw-w-full tw-items-center tw-justify-center tw-py-12'>
          <div className='tw-relative tw-h-40 tw-w-40'>
            <Image
              {...imageProps}
              alt={imageProps.alt || item.title}
              className='tw-h-full tw-w-full tw-object-contain tw-transition-opacity tw-duration-300 group-hover:tw-opacity-0'
              sizes='160px'
              quality={100}
            />
            {inverseImageProps && (
              <Image
                {...inverseImageProps}
                alt={inverseImageProps.alt || item.title}
                className='tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-contain tw-opacity-0 tw-transition-opacity tw-duration-300 group-hover:tw-opacity-100'
                sizes='160px'
                quality={100}
              />
            )}
          </div>
        </div>
      );
    }

    case "chart": {
      const chartData = mainContent.chart;
      if (!chartData) return null;

      if (chartData.chartType === "video") {
        const videoConfig: VideoConfig = {
          type: "video",
          video: chartData.video as string | Media | null | undefined,
          hasAudio: chartData.hasAudio,
        };
        return (
          <ContentWrapper>
            <ChartVideo video={videoConfig} showCaption={false} padding={false} />
          </ContentWrapper>
        );
      }

      // Bar chart
      const barChartConfig: BarChartConfig = {
        type: "bar",
        accessibilityDescription: chartData.accessibilityDescription || "",
        highlightedTheme: chartData.highlightedTheme,
        infoTooltip: chartData.infoTooltip,
        yAxisConfig: chartData.yAxisConfig || { labels: [] },
        dataPoints: chartData.dataPoints || [],
      };
      return (
        <ContentWrapper>
          <BarChart chart={barChartConfig} showCaption={false} padding={false} />
        </ContentWrapper>
      );
    }

    case "component": {
      const componentData = mainContent.component;
      if (!componentData?.template) return null;

      return (
        <ContentWrapper>
          <DemoComponent componentData={componentData} />
        </ContentWrapper>
      );
    }

    default:
      return null;
  }
};
